import React, { useState, useEffect } from "react";
import Header from "../../commons/Header/Header";
import Sidebar from "../../commons/Sidebar/Sidebar";
import { useNavigate } from "react-router-dom";
import { handleApiCall } from "../../apis";
import { endpoints } from "../../utils/endpoints";
import { toast } from "react-toastify";
import { Button } from "react-bootstrap";
import { UI_CONSTANTS } from "../../utils/constants";

function AddAllergenItem() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
  });
  const [ingredients, setIngredients] = useState([]);
  const [selectedIngredients, setSelectedIngredients] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);

  // Fetch ingredients list
  useEffect(() => {
    const fetchIngredients = async () => {
      try {
        const response = await handleApiCall("get", endpoints.ingredientList);
        if (response?.data?.code === 200) {
          setIngredients(response?.data?.data?.data || []);
        }
      } catch (error) {
        console.error("Error fetching ingredients:", error);
      }
    };
    fetchIngredients();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showDropdown && !event.target.closest(".position-relative")) {
        setShowDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showDropdown]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleIngredientSelect = (ingredient) => {
    if (!selectedIngredients.find((item) => item._id === ingredient._id)) {
      setSelectedIngredients([...selectedIngredients, ingredient]);
    }
    setShowDropdown(false);
  };

  const handleIngredientRemove = (ingredientId) => {
    setSelectedIngredients(selectedIngredients.filter((item) => item._id !== ingredientId));
  };

  const getAvailableIngredients = () => {
    return ingredients.filter((ingredient) => !selectedIngredients.find((selected) => selected._id === ingredient._id));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.name.trim()) {
      toast.error(UI_CONSTANTS.TOAST_MESSAGES.VALIDATION.ENTER_ALLERGY_NAME);
      return;
    }

    const nameRegex = /^[A-Za-z\s]+$/;
    if (!nameRegex.test(formData.name.trim())) {
      toast.error("Allergy name should only contain letters and spaces");
      return;
    }

    if (selectedIngredients.length === 0) {
      toast.error(UI_CONSTANTS.TOAST_MESSAGES.VALIDATION.SELECT_INGREDIENTS);
      return;
    }

    setLoading(true);
    try {
      const response = await handleApiCall("post", endpoints.addAllergies, {
        name: formData.name.trim(),
        ingredient_ids: selectedIngredients.map((item) => item._id),
      });

      if (response?.data?.code === UI_CONSTANTS.API_CODES.CREATED) {
        toast.success(response?.data?.message || UI_CONSTANTS.TOAST_MESSAGES.SUCCESS.ALLERGY_ADDED);
        setTimeout(() => {
          navigate(UI_CONSTANTS.ROUTES.ALLERGIES);
        }, UI_CONSTANTS.TIMEOUTS.REDIRECT_DELAY);
      }
      // API errors are already handled by handleApiCall
    } catch (error) {
      console.error("Error adding allergy:", error);
      // Network errors are already handled by handleApiCall
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate(UI_CONSTANTS.ROUTES.ALLERGIES);
  };

  return (
    <div className="container-scroller">
      <Header />
      <div className="container-fluid page-body-wrapper">
        <Sidebar />
        <div className="main-panel">
          <div className="content-wrapper">
            <div className="heading mb-3 d-flex align-items-center">
              <h3 className="text-default font-weight-400 my-0">{UI_CONSTANTS.PAGE_TITLES.ADD_ALLERGY}</h3>
            </div>
            <div className="mb-4 px-0 py-2">
              <div className="card-heading d-flex align-items-center mb-3 pb-2">
                <div className="d-flex align-items-center justify-content-start col-md-12 px-0">
                  <Button className="rounded-lg" onClick={handleSubmit} disabled={loading}>
                    {loading ? UI_CONSTANTS.BUTTON_LABELS.ADDING : UI_CONSTANTS.BUTTON_LABELS.ADD}
                  </Button>
                  <Button className="rounded-lg ml-4 bg-white" onClick={handleCancel} disabled={loading}>
                    {UI_CONSTANTS.BUTTON_LABELS.CANCEL}
                  </Button>
                </div>
              </div>
              <h5 className="text-white mt-4 mb-3">{UI_CONSTANTS.SECTION_HEADERS.ADD_ALLERGEN_ITEM}</h5>
              <form onSubmit={handleSubmit}>
                <div className="row mx-n2">
                  <div className="form-group col-md-6 px-2">
                    <label className="text-white mb-2">{UI_CONSTANTS.FORM_LABELS.ALLERGY_NAME}</label>
                    <input
                      type="text"
                      className="form-control default"
                      placeholder={UI_CONSTANTS.PLACEHOLDERS.ENTER_ALLERGY_NAME}
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      disabled={loading}
                      required
                    />
                  </div>
                  <div className="form-group col-md-6 px-2">
                    <label className="text-white mb-2">{UI_CONSTANTS.FORM_LABELS.INGREDIENT_TYPE}</label>

                    {/* Custom Dropdown */}
                    <div className="position-relative tagsDropdown">
                      <input
                        type="text"
                        className="form-control default"
                        placeholder={UI_CONSTANTS.PLACEHOLDERS.CLICK_TO_SELECT_INGREDIENTS}
                        onClick={() => setShowDropdown(!showDropdown)}
                        readOnly
                        disabled={loading}
                        required
                        style={UI_CONSTANTS.STYLES.DARK_INPUT}
                      />

                      {showDropdown && (
                        <div
                          className="position-absolute w-100 border rounded"
                          style={{ zIndex: UI_CONSTANTS.STYLES.DROPDOWN_Z_INDEX, maxHeight: UI_CONSTANTS.STYLES.DROPDOWN_MAX_HEIGHT, overflowY: "auto" }}
                        >
                          {getAvailableIngredients().length > 0 ? (
                            getAvailableIngredients().map((ingredient) => (
                              <div
                                key={ingredient._id}
                                className="p-2 border-bottom cursor-pointer"
                                onClick={() => handleIngredientSelect(ingredient)}
                                style={{ cursor: "pointer" }}
                              >
                                {ingredient.name}
                              </div>
                            ))
                          ) : (
                            <div className="p-2 text-muted">{UI_CONSTANTS.EMPTY_STATES.NO_INGREDIENTS_AVAILABLE}</div>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Selected Ingredients Display */}
                    {selectedIngredients.length > 0 && (
                      <div className="mt-3">
                        <label className="text-white mb-2">{UI_CONSTANTS.FORM_LABELS.SELECTED_INGREDIENTS}</label>
                        <div className="d-flex flex-wrap gap-2">
                          {selectedIngredients.map((ingredient) => (
                            <span key={ingredient._id} className="badge badge-primary d-flex align-items-center" style={UI_CONSTANTS.STYLES.BADGE_STYLE}>
                              {ingredient.name}
                              <button
                                type="button"
                                className="btn btn-sm p-0 ml-2"
                                onClick={() => handleIngredientRemove(ingredient._id)}
                                disabled={loading}
                                style={{ background: "none", border: "none", color: "white", fontSize: "14px" }}
                              >
                                ×
                              </button>
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AddAllergenItem;
